import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import DcPage from "@/components/landing/dcv2/DcPage";
import SegmentedControl from "@/components/landing/dcv2/SegmentedControl";
import { Briefcase, WifiOff, RefreshCw, X } from "lucide-react";
import { JobPostingsSkeleton } from "@/components/jobs/JobPostingsSkeleton";
import { JobCard } from "@/components/jobs/JobCard";
import { FilterSection } from "@/components/jobs/FilterSection";
import { jobsApi } from "@/services/endpoints/jobs";
import type { JobPostingListItem } from "@/services/api/types";
import { getDateKeyInTimezone, getDisplayTimezone } from "@/utils/timezone";
import { useAuth } from "@/app/hooks/useAuth";
import { useSavedJobs } from "@/app/hooks/useSavedJobs";
import { useToast } from "@/app/hooks/use-toast";

const normalizeValue = (value: string): string => {
  if (!value) return "";
  return value.toLowerCase().trim().replace(/\s*(level|type)\s*/gi, "").replace(/[_\s-]/g, "");
};

const valuesMatch = (a: string, b: string): boolean => {
  if (!a || !b) return false;
  if (a === b || a.toLowerCase() === b.toLowerCase()) return true;
  const na = normalizeValue(a);
  const nb = normalizeValue(b);
  if (na === nb) return true;
  const variants: Record<string, string[]> = {
    fulltime: ["full-time", "fulltime", "full time", "full_time"],
    parttime: ["part-time", "parttime", "part time", "part_time"],
    onsite: ["on-site", "onsite", "on site", "on_site"],
    remote: ["remote"],
    hybrid: ["hybrid"],
    entry: ["entry"],
    mid: ["mid"],
    senior: ["senior"],
    lead: ["lead"],
    director: ["director"],
    executive: ["executive"],
    contract: ["contract"],
    internship: ["internship"],
  };
  const canon = (v: string) => {
    const n = normalizeValue(v);
    for (const [c, list] of Object.entries(variants)) {
      if (n === c || list.includes(n)) return c;
    }
    return n;
  };
  return canon(a) === canon(b);
};

const WORK_TYPES = ["Remote", "Hybrid", "On-site"];
const EMPLOYMENT_TYPES = ["Full Time", "Contract", "Part Time"];
const LEVELS = ["Entry Level", "Mid Level", "Senior Level", "Lead"];

export default function JobPostings() {
  const [jobPostings, setJobPostings] = useState<JobPostingListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // The list filters as you type; Search (or Enter) takes you down to the results.
  const resultsRef = useRef<HTMLElement>(null);
  const showResults = () => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const [visibleCount, setVisibleCount] = useState(5);
  const [selectedWorkTypes, setSelectedWorkTypes] = useState<string[]>([]);
  const [selectedEmploymentTypes, setSelectedEmploymentTypes] = useState<string[]>([]);
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  // Phones only: the filters sit behind a toggle so the roles come first (T101026001).
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");

  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isSaved, toggleSave } = useSavedJobs();
  const { toast } = useToast();

  const handleToggleSave = async (job: JobPostingListItem, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate(`/auth?redirect=${encodeURIComponent("/jobs")}`);
      return;
    }
    try {
      const nowSaved = await toggleSave(job.id);
      toast({
        title: nowSaved ? "Job saved" : "Job removed",
        description: nowSaved
          ? "Find it later under Profile › Saved jobs."
          : "Removed from your saved jobs.",
      });
    } catch {
      toast({ title: "Couldn't update saved jobs", description: "Please try again.", variant: "destructive" });
    }
  };

  const clearFilters = () => {
    setSelectedWorkTypes([]);
    setSelectedEmploymentTypes([]);
    setSelectedLevels([]);
    setSearchTerm("");
  };

  const hasActiveFilters =
    selectedWorkTypes.length > 0 ||
    selectedEmploymentTypes.length > 0 ||
    selectedLevels.length > 0 ||
    searchTerm.trim().length > 0;

  // Re-fetch when auth resolves: the BE only includes per-item `hasApplied`
  // when a candidate token accompanies the request (jobsApi.getAll attaches it).
  useEffect(() => {
    fetchJobPostings();
  }, [isAuthenticated]);

  const fetchJobPostings = async () => {
    try {
      setLoading(true);
      // Pull every open posting (bounded) so the client-side search / filters /
      // sort below operate over the full board, not just the BE's first page.
      const { jobs, truncated: isTruncated } = await jobsApi.fetchAllOpen();
      setJobPostings(jobs);
      setTruncated(isTruncated);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const toggleFilter = (value: string, filters: string[], setFilters: (f: string[]) => void) => {
    if (filters.includes(value)) setFilters(filters.filter((f) => f !== value));
    else setFilters([...filters, value]);
  };

  const filteredJobs = jobPostings.filter((job) => {
    // Expired postings are omitted by GET /public/jobs server-side, so no
    // client-side deadline filtering is needed here.
    const matchesSearch =
      !searchTerm ||
      job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.location.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesWork =
      selectedWorkTypes.length === 0 ||
      selectedWorkTypes.some((f) => valuesMatch(f, job.workplaceType));

    const matchesEmployment =
      selectedEmploymentTypes.length === 0 ||
      selectedEmploymentTypes.some((f) => valuesMatch(f, job.employmentType));

    const matchesLevel =
      selectedLevels.length === 0 ||
      selectedLevels.some((f) => valuesMatch(f, job.seniorityLevel));

    return matchesSearch && matchesWork && matchesEmployment && matchesLevel;
  });

  const sortedJobs = [...filteredJobs].sort((a, b) => {
    const diff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    return sortBy === "newest" ? diff : -diff;
  });

  const displayedJobs = sortedJobs.slice(0, visibleCount);

  // Spotlight the most-recent role on the default, unfiltered view.
  const featuredEligible = !hasActiveFilters && sortBy === "newest" && sortedJobs.length >= 3;

  const formatDate = (d: string) => {
    try {
      const tz = getDisplayTimezone();
      const now = new Date();
      if (getDateKeyInTimezone(d, tz) === getDateKeyInTimezone(now, tz)) return "Today";
      const diff = now.getTime() - new Date(d).getTime();
      const days = Math.floor(diff / 86400000);
      if (days <= 1) return "1 day ago";
      if (days < 7) return `${days} days ago`;
      if (days < 14) return "1 week ago";
      if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
      return `${Math.floor(days / 30)} month${Math.floor(days / 30) > 1 ? "s" : ""} ago`;
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <DcPage>
        <JobPostingsSkeleton />
      </DcPage>
    );
  }

  if (error) {
    return (
      <DcPage>
        <section
          className="site-gutter"
          style={{
            paddingTop: 150,
            paddingBottom: 130,
            position: "relative",
            overflow: "clip",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 620, margin: "0 auto", position: "relative" }}>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: 9,
                background: "var(--chip)",
                color: "#9DF1FF",
                marginBottom: 26,
              }}
            >
              <WifiOff size={24} />
            </span>
            <h1
              style={{
                margin: "0 0 16px",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(28px, 3.4vw, 46px)",
                letterSpacing: "-0.02em",
                lineHeight: 1.14,
              }}
            >
              Unable to load positions
            </h1>
            <p
              style={{
                margin: "0 auto 32px",
                maxWidth: 460,
                fontSize: 16,
                color: "rgba(var(--ink-rgb),.66)",
              }}
            >
              We couldn't connect to the server right now. This is usually
              temporary. Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={fetchJobPostings}
              className="dc-h-btn-lg"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                cursor: "pointer",
                background: "var(--btn)",
                color: "var(--btnfg)",
                border: "none",
                padding: "15px 28px",
                borderRadius: 999,
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 15,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              <RefreshCw size={16} />
              Try Again
            </button>
          </div>
        </section>
      </DcPage>
    );
  }

  return (
    <DcPage>
      {/* HERO */}
      <section className="jobs-hero" style={{ position: "relative", overflow: "clip" }}>
        <div data-rv="" style={{ maxWidth: 1240, margin: "0 auto", position: "relative" }}>
          <div
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: 11.5,
              letterSpacing: ".16em",
              textTransform: "uppercase",
              color: "var(--link)",
              marginBottom: 18,
            }}
          >
            <span className="dc-mark" aria-hidden="true" /> Careers at AI2Aim
          </div>
          <h1
            style={{
              margin: "0 0 16px",
              maxWidth: 760,
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: "clamp(28px, 3.4vw, 46px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.14,
            }}
          >
            Find your next <span style={{ color: "var(--acc2)" }}>opportunity.</span>
          </h1>
          <p style={{ margin: "0 0 34px", maxWidth: 620, fontSize: 16, color: "rgba(var(--ink-rgb),.66)" }}>
            Build the operating system for modern enterprise. Join a team shipping AI agents, financial intelligence, and unified business platforms.
          </p>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              showResults();
            }}
            style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}
          >
            <input
              type="search"
              aria-label="Search by title, location, or keyword"
              placeholder="Search by title, location, or keyword&hellip;"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setVisibleCount(5);
              }}
              style={{
                flex: "1 1 320px",
                maxWidth: 520,
                background: "var(--paper2)",
                border: "1px solid rgba(var(--ink-rgb),.16)",
                borderRadius: 999,
                padding: "10px 18px",
                fontFamily: "'Manrope', sans-serif",
                fontSize: 14,
                color: "var(--ink)",
                outline: "none",
              }}
            />
            <button
              type="submit"
              className="dc-h-btn-sm"
              style={{
                cursor: "pointer",
                background: "var(--btn)",
                color: "var(--btnfg)",
                border: "none",
                borderRadius: 999,
                padding: "11px 26px",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: 14,
                transition: "transform .2s, box-shadow .2s",
              }}
            >
              Search
            </button>
          </form>
        </div>
      </section>

      {/* LIST */}
      <section ref={resultsRef} className="jobs-list-section" style={{ position: "relative", overflow: "clip", scrollMarginTop: 88 }}>
        <div className="jobs-layout">
          {(() => {
            const activeCount = selectedWorkTypes.length + selectedEmploymentTypes.length + selectedLevels.length;
            return (
              <button
                type="button"
                className="jobs-filters-toggle"
                aria-expanded={filtersOpen}
                aria-controls="jobs-filters"
                onClick={() => setFiltersOpen((open) => !open)}
              >
                {filtersOpen ? "Hide filters" : "Filters"}
                {activeCount > 0 ? ` (${activeCount})` : ""}
              </button>
            );
          })()}
          <aside
            id="jobs-filters"
            className={`jobs-filters${filtersOpen ? " is-open" : ""}`}
            style={{
              background: "var(--paper2)",
              border: "1px solid rgba(var(--ink-rgb),.13)",
              borderRadius: 18,
              padding: 28,
            }}
          >
            <div
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--link)",
                marginBottom: 26,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> Filters
            </div>
            <FilterSection
              title="Work Type"
              options={WORK_TYPES}
              selected={selectedWorkTypes}
              onToggle={(v) => toggleFilter(v, selectedWorkTypes, setSelectedWorkTypes)}
            />
            <FilterSection
              title="Job Type"
              options={EMPLOYMENT_TYPES}
              selected={selectedEmploymentTypes}
              onToggle={(v) => toggleFilter(v, selectedEmploymentTypes, setSelectedEmploymentTypes)}
            />
            <FilterSection
              title="Experience Level"
              options={LEVELS}
              selected={selectedLevels}
              onToggle={(v) => toggleFilter(v, selectedLevels, setSelectedLevels)}
              last
            />
          </aside>

          <main style={{ minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 16,
                paddingBottom: 20,
                borderBottom: "1px solid rgba(var(--ink-rgb),.2)",
                marginBottom: 24,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <p
                  style={{
                    margin: 0,
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 12,
                    color: "rgba(var(--ink-rgb),.62)",
                  }}
                >
                  <span style={{ color: "var(--ink)" }}>{sortedJobs.length}</span>{" "}
                  {sortedJobs.length === 1 ? "open role" : "open roles"}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="dc-h-link"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      background: "none",
                      border: "none",
                      padding: 0,
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 12,
                      color: "rgba(var(--ink-rgb),.62)",
                      transition: "color .2s",
                    }}
                  >
                    <X size={12} />
                    Clear
                  </button>
                )}
              </div>
              {/* Was a pair of `aria-pressed` buttons, duplicated from the one
                  Pricing defined for its billing interval. Both are now the
                  shared control: a radiogroup rather than two toggles, and one
                  tab stop with arrow keys rather than a tab stop per option. */}
              <SegmentedControl<"newest" | "oldest">
                label="Sort order"
                value={sortBy}
                onChange={setSortBy}
                segments={[
                  { value: "newest", label: "Newest" },
                  { value: "oldest", label: "Oldest" },
                ]}
              />
            </div>

            {sortedJobs.length > 0 ? (
              <div style={{ display: "grid", gap: 14 }}>
                {displayedJobs.map((job, index) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    featured={featuredEligible && index === 0}
                    saved={isSaved(job.id)}
                    applied={job.hasApplied}
                    onToggleSave={(e) => handleToggleSave(job, e)}
                    formatDate={formatDate}
                  />
                ))}

                {visibleCount < sortedJobs.length && (
                  <div style={{ display: "flex", justifyContent: "center", paddingTop: 18 }}>
                    <button
                      type="button"
                      onClick={() => setVisibleCount(sortedJobs.length)}
                      className="dc-h-ghost"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                        cursor: "pointer",
                        background: "transparent",
                        border: "1px solid rgba(var(--ink-rgb),.3)",
                        borderRadius: 999,
                        padding: "12px 26px",
                        fontFamily: "'Manrope', sans-serif",
                        fontSize: 12,
                        letterSpacing: ".12em",
                        textTransform: "uppercase",
                        color: "rgba(var(--ink-rgb),.66)",
                        transition: "border-color .2s, background .2s, color .2s",
                      }}
                    >
                      Show all {sortedJobs.length} positions
                      <span aria-hidden="true">&darr;</span>
                    </button>
                  </div>
                )}

                {truncated && (
                  <p
                    style={{
                      margin: "10px 0 0",
                      textAlign: "center",
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 12,
                      color: "rgba(var(--ink-rgb),.62)",
                    }}
                  >
                    Showing the most recent {jobPostings.length} roles. Refine your search to narrow results.
                  </p>
                )}
              </div>
            ) : (
              <div
                style={{
                  background: "var(--paper2)",
                  border: "1px solid rgba(var(--ink-rgb),.13)",
                  borderRadius: 18,
                  padding: "64px 28px",
                  textAlign: "center",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 52,
                    height: 52,
                    borderRadius: 9,
                    background: "var(--chip)",
                    color: "#9DF1FF",
                    marginBottom: 22,
                  }}
                >
                  <Briefcase size={22} />
                </span>
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: 18,
                    letterSpacing: "-0.01em",
                  }}
                >
                  {hasActiveFilters ? "No roles match your filters" : "No open roles right now"}
                </h3>
                <p style={{ margin: "0 auto 26px", maxWidth: 420, fontSize: 14.5, color: "rgba(var(--ink-rgb),.66)" }}>
                  {hasActiveFilters
                    ? "Try broadening your search or clearing filters."
                    : "Check back soon, or reach out and tell us where you'd fit."}
                </p>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="dc-h-btn-sm"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      cursor: "pointer",
                      background: "var(--btn)",
                      color: "var(--btnfg)",
                      border: "none",
                      borderRadius: 999,
                      padding: "13px 26px",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      transition: "transform .2s, box-shadow .2s",
                    }}
                  >
                    <X size={14} />
                    Clear filters
                  </button>
                ) : (
                  <Link
                    to="/contact"
                    className="dc-h-btn-sm"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 10,
                      background: "var(--btn)",
                      color: "var(--btnfg)",
                      borderRadius: 999,
                      padding: "13px 26px",
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      transition: "transform .2s, box-shadow .2s",
                    }}
                  >
                    Contact us <span aria-hidden="true">&rarr;</span>
                  </Link>
                )}
              </div>
            )}
          </main>
        </div>
      </section>

      {/* CTA */}
      <section
        className="dc-seam dc-seam-foot"
        style={{
          position: "relative",
          overflow: "clip",
          background: "var(--band)",
          color: "var(--band-ink)",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "50%",
            right: -220,
            width: 640,
            height: 640,
            marginTop: -320,
            border: "1px solid rgba(var(--band-acc-rgb),.18)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />
        <div
          className="site-gutter"
          style={{
            maxWidth: 1240,
            margin: "0 auto",
            paddingTop: 118,
            paddingBottom: 118,
            position: "relative",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              margin: "0 0 18px",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 600,
              fontSize: "clamp(28px, 3.4vw, 46px)",
              letterSpacing: "-0.02em",
              lineHeight: 1.14,
            }}
          >
            Don't see the right role?
          </h2>
          <p style={{ margin: "0 auto 36px", maxWidth: 540, fontSize: 16, color: "rgba(var(--band-ink-rgb),.68)" }}>
            We're always interested in talented engineers, designers, and operators. Reach out and tell us where you'd fit.
          </p>
          <Link
            to="/contact"
            className="dc-h-btn-cyan"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              background: "rgb(var(--band-acc-rgb))",
              color: "var(--band-on-acc)",
              padding: "16px 30px",
              borderRadius: 999,
              fontWeight: 700,
              fontSize: 15.5,
              transition: "transform .2s, box-shadow .2s",
            }}
          >
            Contact Us <span aria-hidden="true">&rarr;</span>
          </Link>
        </div>
      </section>
    </DcPage>
  );
}
